import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { exportToCsv } from '../helpers/exportCsv';
import { alertarErro } from '../helpers/alerts';

const PAGE_SIZE = 15;

export default function Usuarios() {
  const [vinculados, setVinculados] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [perfilFiltro, setPerfilFiltro] = useState('');
  const [pagina, setPagina] = useState(1);

  // Vendedores: vêm do ERP (VENDEDOCAD), não da base do portal. Depois de
  // liberados, são gerenciados pelos mesmos cartões de usuário abaixo — o
  // vínculo é do mesmo tipo.
  const [buscaVend, setBuscaVend] = useState('');
  const [resultadosVend, setResultadosVend] = useState([]);
  const [buscandoVend, setBuscandoVend] = useState(false);
  const [perfilVend, setPerfilVend] = useState('');

  const buscarVendedor = async () => {
    const termo = buscaVend.trim();
    if (!termo) return;
    setBuscandoVend(true);
    try {
      const r = await axios.get(`${API}/usuarios/vendedores/buscar`, { params: { q: termo } });
      setResultadosVend(r.data?.data || []);
    } catch (e) {
      alertarErro(e, 'Buscar vendedor');
    } finally { setBuscandoVend(false); }
  };

  const liberarVendedor = async (v) => {
    try {
      await axios.post(`${API}/usuarios/vendedores/liberar`, {
        codvend: v.codvend, perfil_id: Number(perfilVend),
      });
      Swal.fire({
        icon: 'success',
        title: `${v.nome} liberado`,
        text: 'Ele já entra com o código e a senha do ERP. Agora escolha o canal dele no cartão abaixo.',
      });
      setResultadosVend([]);
      setBuscaVend('');
      await carregar();
    } catch (e) {
      alertarErro(e, 'Liberar vendedor');
    }
  };

  // Vincular novo
  const [buscaBase, setBuscaBase] = useState('');
  const [resultadosBase, setResultadosBase] = useState([]);
  const [buscandoBase, setBuscandoBase] = useState(false);
  const [perfilParaVincular, setPerfilParaVincular] = useState('');

  const [canais, setCanais] = useState([]);
  const [setoresLista, setSetoresLista] = useState([]); // filas de atendimento (Caixa, Crediario, ...)
  // Edicao do usuario num modal unico (perfil + canal padrao + extras + flag).
  const [editUser, setEditUser] = useState(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [form, setForm] = useState({ perfil_id: '', canal_id: '', extras: [], assumir: false, setores: [] });
  const [extrasOpen, setExtrasOpen] = useState(false);

  const carregar = () => {
    setLoading(true);
    setErro('');
    Promise.all([
      axios.get(`${API}/usuarios`),
      axios.get(`${API}/perfis`),
      axios.get(`${API}/atendimento/canais`).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/setores?ativos=true`).catch(() => ({ data: { data: [] } })),
    ])
      .then(([u, p, c, s]) => {
        setVinculados(u.data?.data || []);
        setPerfis(p.data?.data || []);
        setCanais(c.data?.data || []);
        setSetoresLista(s.data?.data || []);
      })
      .catch(() => setErro('Não foi possível carregar os usuários vinculados.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const buscarNaBase = async () => {
    if (buscaBase.trim().length < 2) return;
    setBuscandoBase(true);
    try {
      const res = await axios.get(`${API}/usuarios/buscar`, { params: { nome: buscaBase.trim() } });
      setResultadosBase(res.data?.data || []);
    } catch (err) {
      alertarErro(err, 'Não deu pra buscar na base');
    } finally {
      setBuscandoBase(false);
    }
  };

  const vincular = async (usuarioBase) => {
    if (!perfilParaVincular) {
      Swal.fire({ icon: 'warning', title: 'Selecione um perfil antes de vincular.' });
      return;
    }
    try {
      await axios.post(`${API}/usuarios/vincular`, {
        usuario_id: usuarioBase.id,
        perfil_id: Number(perfilParaVincular),
      });
      Swal.fire({ icon: 'success', title: 'Usuário vinculado', timer: 1500, showConfirmButton: false });
      setBuscaBase('');
      setResultadosBase([]);
      setPerfilParaVincular('');
      carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra buscar na base');
    }
  };

  const abrirEdicao = async (v) => {
    setForm({
      perfil_id: v.perfil_id || '',
      canal_id: v.canal_id || '',
      extras: Array.isArray(v.canais_extra) ? v.canais_extra : [],
      assumir: !!v.assumir_outros_canais,
      setores: [],
      setorPrincipal: null,
      acompanha: [],
    });
    setExtrasOpen(false);
    setEditUser(v);
    // Carrega as filas/setores que o usuario ve (visibilidade por setor).
    try {
      const res = await axios.get(`${API}/setores/usuario/${v.id}`);
      const vinculos = res.data?.data || [];
      setForm((f) => ({
        ...f,
        setores: vinculos.map((r) => r.setor_id),
        setorPrincipal: vinculos.find((r) => r.principal)?.setor_id ?? null,
        acompanha: vinculos.filter((r) => r.so_acompanha).map((r) => r.setor_id),
      }));
    } catch (_) { /* segue sem setores */ }
  };

  // Troca o canal padrao garantindo que ele nao fique tambem como extra.
  const mudarPadrao = (val) => {
    const canalId = val ? Number(val) : null;
    setForm((f) => ({ ...f, canal_id: val, extras: f.extras.filter((x) => x !== canalId) }));
  };

  const toggleExtra = (id) => {
    setForm((f) => {
      const has = f.extras.includes(id);
      const extras = has ? f.extras.filter((x) => x !== id) : [...f.extras, id];
      return { ...f, extras, assumir: extras.length === 0 ? false : f.assumir };
    });
  };

  const toggleSetor = (id) => {
    setForm((f) => {
      const has = f.setores.includes(id);
      const setores = has ? f.setores.filter((x) => x !== id) : [...f.setores, id];
      // Tirou a fila que era a principal? A marcação cai junto (e o "só acompanha" também).
      const setorPrincipal = has && f.setorPrincipal === id ? null : f.setorPrincipal;
      const acompanha = has ? (f.acompanha || []).filter((x) => x !== id) : (f.acompanha || []);
      return { ...f, setores, setorPrincipal, acompanha };
    });
  };

  // Fila principal = onde as conversas dele caem por padrão ao assumir. Clicar
  // de novo na mesma desmarca (volta a não ter padrão).
  // Principal e "só acompanha" se excluem: a principal é a casa dele.
  const marcarPrincipal = (id) => setForm((f) => ({
    ...f,
    setorPrincipal: f.setorPrincipal === id ? null : id,
    setores: f.setores.includes(id) ? f.setores : [...f.setores, id],
    acompanha: f.setorPrincipal === id ? (f.acompanha || []) : (f.acompanha || []).filter((x) => x !== id),
  }));

  // "Só acompanha" (mig 105): vê e pode puxar as conversas da fila, mas fica
  // fora da distribuição automática e dos avisos dela.
  const alternarAcompanha = (id) => setForm((f) => {
    const ja = (f.acompanha || []).includes(id);
    return {
      ...f,
      acompanha: ja ? f.acompanha.filter((x) => x !== id) : [...(f.acompanha || []), id],
      setorPrincipal: !ja && f.setorPrincipal === id ? null : f.setorPrincipal,
    };
  });

  // Filas relevantes pro usuário sendo editado: as dos canais dele (padrão +
  // extras) e as órfãs (sem canal). Uma fila de OUTRO canal só entra na lista se
  // já estiver marcada — pra aparecer com o aviso em vez de sumir calada. Foi
  // exatamente esse buraco que deixou uma atendente de Carangola marcada nas
  // filas da matriz: as filas eram globais antes de terem dono.
  const canaisDoForm = useMemo(() => {
    const ids = new Set((form.extras || []).map(Number));
    if (form.canal_id) ids.add(Number(form.canal_id));
    return ids;
  }, [form.canal_id, form.extras]);

  const filaForaDoCanal = (s) => s.canal_id != null && !canaisDoForm.has(Number(s.canal_id));

  const filasParaEscolher = useMemo(
    () => setoresLista.filter((s) => !filaForaDoCanal(s) || form.setores.includes(s.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setoresLista, canaisDoForm, form.setores],
  );

  const filasErradas = useMemo(
    () => setoresLista.filter((s) => form.setores.includes(s.id) && filaForaDoCanal(s)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setoresLista, canaisDoForm, form.setores],
  );

  const salvarEdicao = async () => {
    if (!editUser) return;
    if (!form.perfil_id) {
      Swal.fire({ icon: 'warning', title: 'Selecione um perfil.' });
      return;
    }
    setSalvandoEdicao(true);
    try {
      if (Number(form.perfil_id) !== Number(editUser.perfil_id)) {
        await axios.put(`${API}/usuarios/${editUser.vinculo_id}/perfil`, { perfil_id: Number(form.perfil_id) });
      }
      const canalId = form.canal_id ? Number(form.canal_id) : null;
      const extras = (form.extras || []).map(Number).filter((id) => id && id !== canalId);
      await axios.put(`${API}/usuarios/${editUser.vinculo_id}/canais`, {
        canal_id: canalId,
        canais_extra: extras,
        assumir_outros_canais: extras.length > 0 ? !!form.assumir : false,
      });
      // Filas/setores que o usuario ve (visibilidade por setor).
      await axios.put(`${API}/setores/usuario/${editUser.id}`, {
        setores: (form.setores || []).map((id) => ({
          setor_id: Number(id),
          principal: Number(id) === Number(form.setorPrincipal),
          so_acompanha: (form.acompanha || []).includes(id),
        })),
      });
      Swal.fire({ icon: 'success', title: 'Usuário atualizado', timer: 1300, showConfirmButton: false });
      setEditUser(null);
      carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra salvar');
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const desvincular = async (v) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: `Desvincular ${v.nome}?`,
      text: 'O usuário perderá o acesso ao Fio.',
      showCancelButton: true,
      confirmButtonText: 'Desvincular',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/usuarios/${v.vinculo_id}`);
      Swal.fire({ icon: 'success', title: 'Desvinculado', timer: 1300, showConfirmButton: false });
      carregar();
    } catch (err) {
      alertarErro(err, 'Não deu pra excluir');
    }
  };

  const vinculadosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return vinculados.filter((v) => {
      if (perfilFiltro && String(v.perfil_id || '') !== String(perfilFiltro)) return false;
      if (!termo) return true;
      return [v.nome, v.email, v.perfil_nome].filter(Boolean).join(' ').toLowerCase().includes(termo);
    });
  }, [busca, perfilFiltro, vinculados]);

  useEffect(() => { setPagina(1); }, [busca, perfilFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(vinculadosFiltrados.length / PAGE_SIZE));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicioIdx = (paginaAtual - 1) * PAGE_SIZE;
  const vinculadosPagina = vinculadosFiltrados.slice(inicioIdx, inicioIdx + PAGE_SIZE);

  const handleExport = () => {
    const colunas = [
      { label: 'Nome', value: (v) => v.nome || '' },
      { label: 'Filial', value: (v) => v.filial || '' },
      { label: 'Email', value: (v) => v.email || '' },
      { label: 'Perfil', value: (v) => v.perfil_nome || '' },
    ];
    const hoje = new Date().toISOString().slice(0, 10);
    exportToCsv(`usuarios_portal_ti_${hoje}`, colunas, vinculadosFiltrados);
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Administração</span>
          <h1>Usuários</h1>
          <p>Conceda acesso ao Fio buscando usuários da base do Centrão e atribuindo um perfil.</p>
        </div>
      </section>

      <section className="content-card">
        <div className="section-header section-header--compact">
          <div>
            <span className="section-header__eyebrow">Vincular</span>
            <h2>Conceder acesso a um usuário da base</h2>
          </div>
        </div>

        <div className="filter-card" style={{ marginBottom: 12 }}>
          <div className="filter-card__field filter-card__field--grow">
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Buscar por nome na base do Centrão (ex: DAVYD)"
              value={buscaBase}
              onChange={(e) => setBuscaBase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarNaBase()}
            />
          </div>
          <div className="filter-card__field">
            <select value={perfilParaVincular} onChange={(e) => setPerfilParaVincular(e.target.value)}>
              <option value="">Selecione o perfil...</option>
              {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn-portal btn-portal--primary"
            onClick={buscarNaBase}
            disabled={buscandoBase || buscaBase.trim().length < 2}
          >
            <i className="bi bi-search" /> {buscandoBase ? ' Buscando...' : ' Buscar'}
          </button>
        </div>

        {resultadosBase.length > 0 && (
          <div className="base-results">
            {resultadosBase.map((u) => (
              <div key={u.id} className="base-result">
                <div className="base-result__info">
                  <span className="cell-icon"><i className="bi bi-person" /></span>
                  <div>
                    <strong>{u.nome}</strong>
                    <small>{u.filial ? `Filial ${u.filial}` : 'Sem filial'}</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-portal btn-portal--outline"
                  onClick={() => vincular(u)}
                  disabled={!perfilParaVincular}
                >
                  <i className="bi bi-link-45deg" /> Vincular
                </button>
              </div>
            ))}
          </div>
        )}
        {resultadosBase.length === 0 && buscaBase.trim().length >= 2 && !buscandoBase && (
          <p className="content-card__hint">Nenhum usuário encontrado na base ou todos já estão vinculados.</p>
        )}
      </section>

      {/* Vendedores: identidade no ERP, acesso concedido aqui. */}
      <section className="content-card">
        <div className="section-header section-header--compact">
          <div>
            <span className="section-header__eyebrow">Vendedores</span>
            <h2>Liberar acesso a um vendedor</h2>
          </div>
        </div>
        <p className="content-card__hint" style={{ marginTop: 0 }}>
          O vendedor não está na base do Centrão — ele vem do ERP. Entra no Fio com o
          <strong> código dele</strong> e a <strong>mesma senha do ERP</strong>. Depois de liberado,
          o cartão dele aparece na lista abaixo pra escolher canal e fila.
        </p>

        <div className="filter-card" style={{ marginBottom: 12 }}>
          <div className="filter-card__field filter-card__field--grow">
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Código (ex: 4) ou nome do vendedor"
              value={buscaVend}
              onChange={(e) => setBuscaVend(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarVendedor()}
            />
          </div>
          <div className="filter-card__field">
            <select value={perfilVend} onChange={(e) => setPerfilVend(e.target.value)}>
              <option value="">Selecione o perfil...</option>
              {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn-portal btn-portal--primary"
            onClick={buscarVendedor}
            disabled={buscandoVend || !buscaVend.trim()}
          >
            <i className="bi bi-search" /> {buscandoVend ? ' Buscando...' : ' Buscar'}
          </button>
        </div>

        {resultadosVend.length > 0 && (
          <div className="base-results">
            {resultadosVend.map((v) => (
              <div key={v.codvend} className="base-result">
                <div className="base-result__info">
                  <span className="cell-icon"><i className="bi bi-person-badge" /></span>
                  <div>
                    <strong>{v.nome}</strong>
                    <small>
                      Código {v.codvend}
                      {v.filial ? ` · Filial ${v.filial}` : ''}
                      {v.liberado ? ` · já tem acesso${v.canal_nome ? ` (${v.canal_nome})` : ''}` : ''}
                    </small>
                  </div>
                </div>
                {v.liberado ? (
                  <span style={{ fontSize: '0.8rem', color: '#3D8B37', fontWeight: 600 }}>
                    <i className="bi bi-check-circle me-1" />Liberado
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn-portal btn-portal--outline"
                    onClick={() => liberarVendedor(v)}
                    disabled={!perfilVend}
                    title={!perfilVend ? 'Escolha o perfil primeiro' : `Liberar ${v.nome}`}
                  >
                    <i className="bi bi-key" /> Liberar acesso
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {resultadosVend.length === 0 && buscaVend.trim() && !buscandoVend && (
          <p className="content-card__hint">Nenhum vendedor ativo com esse código ou nome.</p>
        )}
      </section>

      <div className="filter-card">
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Filtrar vinculados por nome, email ou perfil"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="filter-card__field">
          <select value={perfilFiltro} onChange={(e) => setPerfilFiltro(e.target.value)}>
            <option value="">Todos perfis</option>
            {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <div className="table-toolbar">
        <span className="table-toolbar__count">{vinculadosFiltrados.length} usuário(s) vinculado(s)</span>
        <button type="button" className="btn-portal btn-portal--outline" onClick={handleExport} disabled={!vinculadosFiltrados.length}>
          <i className="bi bi-file-earmark-excel" /> Exportar Excel
        </button>
      </div>

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando vínculos...</p>
          </div>
        ) : vinculadosPagina.length ? (
          <>
            <div className="table-shell">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Perfil</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {vinculadosPagina.map((v) => (
                    <tr key={v.vinculo_id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="cell-icon"><i className="bi bi-person" /></span>
                          <span className="cell-strong">{v.nome}</span>
                        </div>
                      </td>
                      {/* Vendedor não tem email: ele vem do ERP e entra pelo
                          código. Mostrar o código no lugar diz o que a pessoa
                          precisa saber — um traço não diz nada. */}
                      <td className="cell-muted">
                        {v.vendedor
                          ? <span title="Entra com o código e a senha do ERP">Vendedor · código {v.codvend}</span>
                          : (v.email || '-')}
                      </td>
                      <td>
                        <span className="permissao-chip permissao-chip--on">{v.perfil_nome || 'sem perfil'}</span>
                      </td>
                      <td className="cell-chevron">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            className="icon-action"
                            title="Alterar usuário"
                            onClick={() => abrirEdicao(v)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="icon-action"
                            title="Desvincular do Fio (mantém usuário na base)"
                            onClick={() => desvincular(v)}
                          >
                            <i className="bi bi-x-circle" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button type="button" className="pagination__btn" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={paginaAtual <= 1}>
                <i className="bi bi-chevron-left" /> Anterior
              </button>
              <span className="pagination__info">Página {paginaAtual} de {totalPaginas}</span>
              <button type="button" className="pagination__btn" onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={paginaAtual >= totalPaginas}>
                Próxima <i className="bi bi-chevron-right" />
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <strong>Nenhum usuário vinculado</strong>
            <span>Use a busca acima para conceder acesso a um usuário da base.</span>
          </div>
        )}
      </section>

      {editUser && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 }}
          onClick={() => !salvandoEdicao && setEditUser(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--portal-surface)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="bi bi-person-gear me-2" />Alterar — {editUser.nome}</strong>
              <button type="button" className="btn-close" onClick={() => setEditUser(null)} disabled={salvandoEdicao} />
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                Filial {editUser.filial || '-'} · {editUser.vendedor
                  ? `vendedor, código ${editUser.codvend}`
                  : (editUser.email || 'sem email')}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Perfil</span>
                <select
                  value={form.perfil_id || ''}
                  onChange={(e) => setForm((f) => ({ ...f, perfil_id: e.target.value ? Number(e.target.value) : '' }))}
                  style={{ width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', outline: 'none', fontSize: '0.85rem' }}
                >
                  <option value="">Selecione um perfil...</option>
                  {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Canal padrão</span>
                <select
                  value={form.canal_id || ''}
                  onChange={(e) => mudarPadrao(e.target.value)}
                  style={{ width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', outline: 'none', fontSize: '0.85rem' }}
                >
                  <option value="">— Sem canal —</option>
                  {canais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                <small style={{ color: 'var(--portal-muted)' }}>
                  Onde ele atende por padrão e de onde saem as conversas que ele inicia.
                </small>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Canais extras (também pode ver)</span>
                {!form.canal_id ? (
                  <small style={{ color: 'var(--portal-muted)' }}>Defina um canal padrão para liberar canais extras.</small>
                ) : canais.filter((c) => c.id !== Number(form.canal_id)).length === 0 ? (
                  <small style={{ color: 'var(--portal-muted)' }}>Não há outros canais.</small>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setExtrasOpen((o) => !o)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      <span>{form.extras.length ? `${form.extras.length} canal(is) selecionado(s)` : 'Selecionar canais...'}</span>
                      <i className={`bi bi-chevron-${extrasOpen ? 'up' : 'down'}`} />
                    </button>
                    {extrasOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--portal-border)', borderRadius: 8, padding: 10, background: 'var(--portal-surface-soft)' }}>
                        {canais.filter((c) => c.id !== Number(form.canal_id)).map((c) => (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              style={{ accentColor: 'var(--portal-blue)' }}
                              checked={form.extras.includes(c.id)}
                              onChange={() => toggleExtra(c.id)}
                            />
                            <i className="bi bi-whatsapp" style={{ color: '#25D366', fontSize: '0.8rem' }} />
                            {c.nome}
                          </label>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', opacity: form.extras.length === 0 ? 0.5 : 1, cursor: form.extras.length === 0 ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ accentColor: '#3D8B37' }}
                  disabled={form.extras.length === 0}
                  checked={form.assumir}
                  onChange={(e) => setForm((f) => ({ ...f, assumir: e.target.checked }))}
                />
                <span>
                  <strong>Assumir/responder</strong> atendimentos dos canais extras
                  <br />
                  <small style={{ color: 'var(--portal-muted)' }}>Desligado = só visualiza os extras; ligado = também atua neles.</small>
                </span>
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem', borderTop: '1px solid var(--portal-border)', paddingTop: 12 }}>
                <span style={{ fontWeight: 600 }}>
                  <i className="bi bi-inboxes me-1" style={{ color: '#F5A623' }} />Filas (setores) que ele vê e atende
                </span>
                {filasErradas.length > 0 && (
                  <div style={{ padding: 8, borderRadius: 8, background: 'rgba(229,72,77,0.12)', border: '1px solid rgba(229,72,77,0.45)', fontSize: '0.78rem' }}>
                    <i className="bi bi-exclamation-triangle me-1" style={{ color: '#E5484D' }} />
                    <strong>Fila de outro canal marcada:</strong>{' '}
                    {filasErradas.map((s) => `${s.nome} (${s.canal_nome || 'sem canal'})`).join(', ')}.
                    <br />
                    Esse usuário não atende esse número, então essa fila nunca vai trazer conversa pra ele.
                    Desmarque e escolha a fila do canal dele.
                  </div>
                )}
                {filasParaEscolher.length === 0 ? (
                  <small style={{ color: 'var(--portal-muted)' }}>
                    {setoresLista.length === 0
                      ? 'Nenhuma fila cadastrada ainda.'
                      : 'Nenhuma fila no canal deste usuário. Escolha o canal acima ou cadastre a fila em Atendimento → Filas.'}
                  </small>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid var(--portal-border)', borderRadius: 8, padding: 10, background: 'var(--portal-surface-soft)' }}>
                    {/* Fila pertence a um canal (número) — agrupa pra não confundir
                        filas de mesmo nome em canais diferentes. */}
                    {Object.entries(filasParaEscolher.reduce((acc, s) => {
                      const chave = s.canal_nome || 'Sem canal';
                      (acc[chave] = acc[chave] || []).push(s);
                      return acc;
                    }, {})).map(([canalNome, filasDoCanal]) => (
                      <div key={canalNome}>
                        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', fontWeight: 600, marginBottom: 3 }}>
                          <i className="bi bi-phone me-1" />{canalNome}
                        </div>
                        {filasDoCanal.map((s) => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                              <input
                                type="checkbox"
                                style={{ accentColor: filaForaDoCanal(s) ? '#E5484D' : '#F5A623' }}
                                checked={form.setores.includes(s.id)}
                                onChange={() => toggleSetor(s.id)}
                              />
                              <i className="bi bi-inboxes" style={{ color: filaForaDoCanal(s) ? '#E5484D' : '#F5A623', fontSize: '0.8rem' }} />
                              <span style={{ color: filaForaDoCanal(s) ? '#E5484D' : 'inherit' }}>
                                {s.nome}
                                {filaForaDoCanal(s) && ' — canal que ele não atende'}
                              </span>
                            </label>
                            {form.setores.includes(s.id) && (
                              <button
                                type="button"
                                onClick={() => marcarPrincipal(s.id)}
                                title={form.setorPrincipal === s.id
                                  ? 'Fila principal: é aqui que as conversas dele caem ao assumir'
                                  : 'Marcar como fila principal'}
                                style={{
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  color: form.setorPrincipal === s.id ? '#F5A623' : 'var(--portal-muted)',
                                  fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 6px',
                                }}
                              >
                                <i className={`bi ${form.setorPrincipal === s.id ? 'bi-star-fill' : 'bi-star'}`} />
                                {form.setorPrincipal === s.id ? 'principal' : ''}
                              </button>
                            )}
                            {form.setores.includes(s.id) && (
                              <button
                                type="button"
                                onClick={() => alternarAcompanha(s.id)}
                                title={(form.acompanha || []).includes(s.id)
                                  ? 'Só acompanha: vê e pode puxar as conversas desta fila, mas não entra na distribuição automática nem recebe os avisos dela'
                                  : 'Marcar como "só acompanha"'}
                                style={{
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  color: (form.acompanha || []).includes(s.id) ? '#3D8B37' : 'var(--portal-muted)',
                                  fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 6px',
                                }}
                              >
                                <i className={`bi ${(form.acompanha || []).includes(s.id) ? 'bi-eye-fill' : 'bi-eye'}`} />
                                {(form.acompanha || []).includes(s.id) ? 'só acompanha' : ''}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                <small style={{ color: 'var(--portal-muted)' }}>
                  A <strong>estrela</strong> marca a fila principal: é nela que a conversa cai quando
                  ele assume ou responde algo que ainda não tinha fila. Sem estrela, quem vê várias
                  filas precisa escolher na mão a cada vez.
                  <br />
                  O <strong>olho</strong> marca <strong>só acompanha</strong>: ele vê e pode puxar as
                  conversas da fila, mas não entra na distribuição automática nem recebe os avisos dela.
                  <br />
                  Marque as filas que este usuário enxerga. As filas são <strong>por canal</strong> (número):
                  quem atende dois números marca filas dos dois. Ex.: Crediário marca
                  <strong> Caixa + Crediário</strong>; Caixa marca <strong>só Caixa</strong>.
                  Conversas sem fila (pendente) aparecem para todos.
                </small>
              </div>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-portal btn-portal--outline" onClick={() => setEditUser(null)} disabled={salvandoEdicao}>
                Cancelar
              </button>
              <button type="button" className="btn-portal btn-portal--primary" onClick={salvarEdicao} disabled={salvandoEdicao}>
                {salvandoEdicao ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
